import React, { useMemo } from 'react'
import styles from './ManageUserPage.module.scss'
import { Trans, useTranslation } from 'react-i18next'
import useUserLearningItems from '../../state/queries/useUserLearningItems'
import { LearningItem } from '../../models/LearningItem'
import { Link } from 'react-router-dom'
import LearningItemImage from '../../components/LearningItemImage/LearningItemImage'
import useRoutes from '../../state/useRoutes'
import {
  getNearestFutureEventDateForTrainer,
  getTrainerFutureLearningItems,
  sortTrainerLearningItemsByStartDate,
} from '../../state/calculations/learningItemHelpers'

export default React.memo(function ManageUserPageTrainerAssignments({ uuid }: { uuid: string }) {
  const { t } = useTranslation()

  const { userLearningItems } = useUserLearningItems(uuid)

  const trainerLearningItems = useMemo(
    () => getTrainerFutureLearningItems(userLearningItems, uuid),
    [userLearningItems, uuid]
  )

  const sortedTrainerLearningItems = useMemo(
    () => sortTrainerLearningItemsByStartDate(trainerLearningItems, uuid),
    [trainerLearningItems]
  )

  return (
    sortedTrainerLearningItems.length > 0 && (
      <section className={styles.ManageUserPageTrainerAssignments}>
        <header>
          <h2>{t('trainer assignments')}</h2>
        </header>
        <main>
          {sortedTrainerLearningItems.map((learningItem) => (
            <TrainerLearningItem key={learningItem.uuid} learningItem={learningItem} trainerUuid={uuid} />
          ))}
          {!trainerLearningItems.length && <span className={styles.empty}>{t('no trainer assignments found')}</span>}
        </main>
      </section>
    )
  )
})

/**********************************************************************
 * TrainerLearningItem
 **********************************************************************/
const TrainerLearningItem = React.memo(function TrainerAssignment({
  learningItem,
  trainerUuid,
}: {
  learningItem: Extract<LearningItem, { kind: 'scheduledCourse' }>
  trainerUuid: string
}) {
  const { t } = useTranslation()
  const routes = useRoutes()

  return (
    <section>
      <div className={styles.information}>
        <header>
          <h1>
            <Link to={routes.course.url({ uuid: learningItem.uuid })}>{learningItem.title}</Link>
          </h1>
          <span>{t(`learning item.type.${learningItem.type}`)}</span>
        </header>

        <section className={styles.assignment}></section>

        <section className={styles.activity}>
          <h1>{t('activity')}</h1>
          <NextTrainerEventDate learningItem={learningItem} trainerUuid={trainerUuid} />
        </section>
      </div>

      <div className={styles.imageAndDueDate}>
        <Link to={routes.course.url({ uuid: learningItem.uuid })}>
          <LearningItemImage learningItem={learningItem} className={styles.coverImage} />
        </Link>
      </div>
    </section>
  )
})

const NextTrainerEventDate = React.memo(function NextEventDate({
  learningItem,
  trainerUuid,
}: {
  learningItem: Extract<LearningItem, { kind: 'scheduledCourse' }>
  trainerUuid: string
}) {
  const { t } = useTranslation()

  const nextEventDate = getNearestFutureEventDateForTrainer(learningItem, trainerUuid)

  return (
    <div className={styles.onlyTime}>
      <Trans t={t} i18nKey="learning item.next session {at}">
        {{ at: nextEventDate }}
      </Trans>
    </div>
  )
})
